import { createApi } from "@reduxjs/toolkit/query/react";

import { intelligenceBaseQuery } from "~/services/intelligenceBaseApi";

export interface TimeSeriesNode {
	date: string;
	frequency: string;
	seriesId: string;
	seriesName: string;
	unit: string;
	value: number;
}

export interface TimeSeriesStats {
	latestValue: number;
	sparklineData: number[];
	trendDirection: "down" | "flat" | "up";
	yearOverYearChange: number;
}

export interface TenantWeatherSettings {
	stationId: string;
	stationName: string;
}

export interface UpdateTenantWeatherStationPayload {
	errors: Array<{ message: string }> | null;
	tenantWeatherSettings: TenantWeatherSettings | null;
}

export interface WeatherStatsResult {
	currentGdd: number;
	currentTemp: number;
	frostRisk: string;
	gddAccumulation: Array<{ accumulatedGdd: number; dayOfYear: number }>;
	gddHistoricalBand: Array<{ average: number; dayOfYear: number; lower: number; upper: number }>;
	hasData: boolean;
	monthlyFrostRisk: Array<{ day: number; risk: string }>;
	seasonPaceDays: number;
	tempHigh: number;
	tempLow: number;
}

export function transformTimeSeriesResponse(response: { timeSeries: { nodes: TimeSeriesNode[] } }): TimeSeriesNode[] {
	return response.timeSeries.nodes;
}

export function transformTimeSeriesStatsResponse(response: { timeSeriesStats: TimeSeriesStats }): TimeSeriesStats {
	return {
		latestValue: response.timeSeriesStats.latestValue ?? 0,
		sparklineData: response.timeSeriesStats.sparklineData ?? [],
		trendDirection: response.timeSeriesStats.trendDirection ?? "flat",
		yearOverYearChange: response.timeSeriesStats.yearOverYearChange ?? 0
	};
}

export interface IngestionStatus {
	errorMessage: string | null;
	lastFailureUtc: string | null;
	lastSuccessUtc: string | null;
	recordCount: number;
	source: string;
}

export function transformIngestionStatusResponse(response: {
	ingestionStatuses: { nodes: IngestionStatus[] };
}): IngestionStatus[] {
	return response.ingestionStatuses.nodes;
}

export function transformWeatherStatsResponse(response: { weatherStats: WeatherStatsResult }): WeatherStatsResult {
	return {
		currentGdd: response.weatherStats.currentGdd ?? 0,
		currentTemp: response.weatherStats.currentTemp ?? 0,
		frostRisk: (response.weatherStats.frostRisk ?? "none").toLowerCase(),
		gddAccumulation: response.weatherStats.gddAccumulation ?? [],
		gddHistoricalBand: response.weatherStats.gddHistoricalBand ?? [],
		hasData: response.weatherStats.hasData ?? false,
		monthlyFrostRisk: response.weatherStats.monthlyFrostRisk ?? [],
		seasonPaceDays: response.weatherStats.seasonPaceDays ?? 0,
		tempHigh: response.weatherStats.tempHigh ?? 0,
		tempLow: response.weatherStats.tempLow ?? 0
	};
}

export interface NewsArticle {
	excerpt: string;
	id: string;
	publishDate: string;
	source: string;
	title: string;
	topicTags: string[];
	url: string;
}

export function transformNewsArticlesResponse(response: { newsArticles: { nodes: NewsArticle[] } }): NewsArticle[] {
	return response.newsArticles.nodes;
}

export interface FootTrafficComparison {
	currentYear: Array<{ month: string; value: number }>;
	currentYearLabel: number;
	previousYear: Array<{ month: string; value: number }>;
	previousYearLabel: number;
}

export interface CostPressureResult {
	color: string;
	label: string;
	value: number;
}

export interface PriceCostSpreadResult {
	costIndex: number;
	retailIndex: number;
	spread: number;
	trend: string;
}

export interface InsightResult {
	text: string;
}

export function transformInsightResponse(response: {
	insights: { nodes: Array<{ insightText: string }> };
}): InsightResult {
	const node = response.insights.nodes[0];
	return {
		text: node?.insightText ?? ""
	};
}

export function transformFootTrafficResponse(response: {
	footTrafficComparison: FootTrafficComparison;
}): FootTrafficComparison {
	return response.footTrafficComparison;
}

export function transformCostPressureResponse(response: { costPressureIndex: CostPressureResult }): CostPressureResult {
	return response.costPressureIndex;
}

export function transformPriceCostSpreadResponse(response: {
	priceCostSpread: PriceCostSpreadResult;
}): PriceCostSpreadResult {
	return response.priceCostSpread;
}

/** Cache durations in seconds for RTK Query keepUnusedDataFor */
const CACHE_5_MIN = 300;
const CACHE_10_MIN = 600;
const CACHE_1_HOUR = 3600;

export const intelligenceApi = createApi({
	baseQuery: intelligenceBaseQuery,
	endpoints: (builder) => ({
		getCostPressureIndex: builder.query<CostPressureResult, void>({
			keepUnusedDataFor: CACHE_5_MIN,
			query: () => ({
				document: `
					query GetCostPressureIndex {
						costPressureIndex {
							value
							label
							color
						}
					}
				`
			}),
			transformResponse: transformCostPressureResponse
		}),
		getFootTrafficComparison: builder.query<FootTrafficComparison, { region?: string }>({
			keepUnusedDataFor: CACHE_5_MIN,
			query: ({ region }) => ({
				document: `
					query GetFootTrafficComparison($region: String) {
						footTrafficComparison(region: $region) {
							currentYear { month value }
							previousYear { month value }
							currentYearLabel
							previousYearLabel
						}
					}
				`,
				variables: { region: region ?? null }
			}),
			transformResponse: transformFootTrafficResponse
		}),
		getIngestionStatuses: builder.query<IngestionStatus[], void>({
			query: () => ({
				document: `
					query GetIngestionStatuses {
						ingestionStatuses {
							nodes {
								source
								lastSuccessUtc
								lastFailureUtc
								recordCount
								errorMessage
							}
						}
					}
				`
			}),
			transformResponse: transformIngestionStatusResponse
		}),
		getInsightForSection: builder.query<InsightResult, string>({
			keepUnusedDataFor: CACHE_1_HOUR,
			query: (section) => ({
				document: `
					query GetInsightForSection($section: String!) {
						insights(where: { section: { eq: $section } }, first: 1) {
							nodes {
								insightText
							}
						}
					}
				`,
				variables: { section }
			}),
			transformResponse: transformInsightResponse
		}),
		getNewsArticles: builder.query<NewsArticle[], void>({
			keepUnusedDataFor: CACHE_5_MIN,
			query: () => ({
				document: `
					query GetNewsArticles {
						newsArticles(
							first: 50
							order: { publishDate: DESC }
						) {
							nodes {
								id
								title
								source
								url
								publishDate
								excerpt
								topicTags
							}
						}
					}
				`
			}),
			transformResponse: transformNewsArticlesResponse
		}),
		getPriceCostSpread: builder.query<PriceCostSpreadResult, void>({
			keepUnusedDataFor: CACHE_5_MIN,
			query: () => ({
				document: `
					query GetPriceCostSpread {
						priceCostSpread {
							retailIndex
							costIndex
							spread
							trend
						}
					}
				`
			}),
			transformResponse: transformPriceCostSpreadResponse
		}),
		getTenantWeatherSettings: builder.query<TenantWeatherSettings, void>({
			keepUnusedDataFor: CACHE_10_MIN,
			providesTags: ["TenantWeatherSettings"],
			query: () => ({
				document: `
					query GetTenantWeatherSettings {
						tenantWeatherSettings(first: 1) {
							nodes {
								stationId
								stationName
							}
						}
					}
				`
			}),
			transformResponse: (response: { tenantWeatherSettings: { nodes: TenantWeatherSettings[] } }) =>
				response.tenantWeatherSettings.nodes[0] ?? { stationId: "", stationName: "" }
		}),
		getTimeSeries: builder.query<TimeSeriesNode[], { limit?: number; seriesId: string }>({
			keepUnusedDataFor: CACHE_5_MIN,
			query: ({ limit = 24, seriesId }) => ({
				document: `
					query GetTimeSeries($seriesId: String!, $limit: Int!) {
						timeSeries(
							where: { seriesId: { eq: $seriesId }, source: { eq: "FRED" } }
							order: { date: DESC }
							first: $limit
						) {
							nodes {
								date
								value
								seriesId
								seriesName
								unit
								frequency
							}
						}
					}
				`,
				variables: { limit, seriesId }
			}),
			transformResponse: transformTimeSeriesResponse
		}),
		getTimeSeriesForExport: builder.query<TimeSeriesNode[], { seriesId: string }>({
			keepUnusedDataFor: CACHE_5_MIN,
			query: ({ seriesId }) => ({
				document: `
					query GetTimeSeriesForExport($seriesId: String!, $limit: Int!) {
						timeSeries(
							where: { seriesId: { eq: $seriesId }, source: { eq: "FRED" } }
							order: { date: DESC }
							first: $limit
						) {
							nodes {
								date
								value
								seriesId
								seriesName
								unit
								frequency
							}
						}
					}
				`,
				// Max 5000 data points per export — sufficient for daily data over ~13 years
				variables: { limit: 5000, seriesId }
			}),
			transformResponse: transformTimeSeriesResponse
		}),
		getTimeSeriesStats: builder.query<TimeSeriesStats, { seriesId: string }>({
			keepUnusedDataFor: CACHE_5_MIN,
			query: ({ seriesId }) => ({
				document: `
					query GetTimeSeriesStats($seriesId: String!) {
						timeSeriesStats(seriesId: $seriesId) {
							latestValue
							yearOverYearChange
							trendDirection
							sparklineData
						}
					}
				`,
				variables: { seriesId }
			}),
			transformResponse: transformTimeSeriesStatsResponse
		}),
		getWeatherStats: builder.query<WeatherStatsResult, { stationId: string }>({
			keepUnusedDataFor: CACHE_10_MIN,
			query: ({ stationId }) => ({
				document: `
					query GetWeatherStats($stationId: String!) {
						weatherStats(stationId: $stationId) {
							hasData
							currentTemp
							tempHigh
							tempLow
							frostRisk
							seasonPaceDays
							currentGdd
							gddAccumulation { dayOfYear accumulatedGdd }
							gddHistoricalBand { dayOfYear average upper lower }
							monthlyFrostRisk { day risk }
						}
					}
				`,
				variables: { stationId }
			}),
			transformResponse: transformWeatherStatsResponse
		}),
		updateTenantWeatherStation: builder.mutation<
			UpdateTenantWeatherStationPayload,
			{ stationId: string; stationName: string }
		>({
			invalidatesTags: ["TenantWeatherSettings"],
			query: ({ stationId, stationName }) => ({
				document: `
					mutation UpdateTenantWeatherStation($stationId: String!, $stationName: String!) {
						updateTenantWeatherStation(input: { stationId: $stationId, stationName: $stationName }) {
							tenantWeatherSettings {
								stationId
								stationName
							}
							errors {
								message
							}
						}
					}
				`,
				variables: { stationId, stationName }
			}),
			transformResponse: (response: { updateTenantWeatherStation: UpdateTenantWeatherStationPayload }) =>
				response.updateTenantWeatherStation
		})
	}),
	reducerPath: "intelligenceApi",
	tagTypes: ["TenantWeatherSettings"]
});

export const {
	useGetCostPressureIndexQuery,
	useGetFootTrafficComparisonQuery,
	useGetIngestionStatusesQuery,
	useGetInsightForSectionQuery,
	useGetNewsArticlesQuery,
	useGetPriceCostSpreadQuery,
	useGetTenantWeatherSettingsQuery,
	useGetTimeSeriesQuery,
	useGetTimeSeriesStatsQuery,
	useGetWeatherStatsQuery,
	useLazyGetTimeSeriesForExportQuery,
	useUpdateTenantWeatherStationMutation
} = intelligenceApi;
